import { CheckBadgeIcon, CheckCircleIcon } from "@heroicons/react/24/outline";
import ServiceCategoryPill from "../ServiceCategoryPill";
import { MyVendorRiskScoreBadge } from "./MyVendorRiskScoreBadge";
import {
  formatRequestRowDate,
  getMyVendorRequestDisplayTitle,
  getRequestTypeFamily,
  resolveRequestGxps,
  shortenRequestLocation,
} from "../../utils/myVendorsRequestUtils";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

function isScoreEligibleRequest(request) {
  return request?.type === "RFI" && request?.isCompleted;
}

export function MyVendorRequestBadgeIcon({ type, inverted = false }) {
  const family = getRequestTypeFamily(type);
  const isVqa = family === "VQA";
  const Icon = isVqa ? CheckBadgeIcon : CheckCircleIcon;

  if (inverted) {
    const bg = isVqa ? "bg-rose-600" : "bg-indigo-600";
    return (
      <span
        className={classNames(
          "inline-flex flex-shrink-0 items-center justify-center rounded p-1 text-white",
          bg
        )}
      >
        <Icon className="h-3.5 w-3.5" />
      </span>
    );
  }

  const bg = isVqa ? "bg-rose-100" : "bg-indigo-100";
  const text = isVqa ? "text-rose-700" : "text-indigo-700";
  return (
    <span
      className={classNames(
        "inline-flex flex-shrink-0 items-center justify-center rounded p-1",
        bg,
        text
      )}
    >
      <Icon className="h-3.5 w-3.5" />
    </span>
  );
}

/** GxP pill(s) first, then up to 3 service pills, then +N — prototype ChipRow parity. */
function RequestBadgeChipRow({ gxps = [], services = [] }) {
  const gxpList = (gxps || []).filter(Boolean);
  const serviceList = (services || []).filter(Boolean).map((s) => String(s).trim()).filter(Boolean);

  if (gxpList.length === 0 && serviceList.length === 0) return null;

  return (
    <div className="mt-1 flex flex-wrap items-center gap-1">
      {gxpList.map((gxp) => (
        <span
          key={`g-${gxp}`}
          className="inline-flex items-center rounded bg-slate-500 px-1.5 py-0.5 text-[10px] font-medium text-white"
        >
          {gxp}
        </span>
      ))}
      {serviceList.slice(0, 3).map((service) => (
        <ServiceCategoryPill key={`s-${service}`} name={service} className="mb-0.5" />
      ))}
      {serviceList.length > 3 ? (
        <span className="text-[10px] text-gray-500">+{serviceList.length - 3}</span>
      ) : null}
    </div>
  );
}

/** Single request row for My Vendors card tabs (prototype RequestRow parity). */
export default function MyVendorRequestBadge({ request, onClick, inverted = false }) {
  const resolvedGxps = resolveRequestGxps(request);
  const titleStripped = getMyVendorRequestDisplayTitle(request);
  const dateStr = formatRequestRowDate(request?.date);
  const locationShort = shortenRequestLocation(request?.location);
  const showScoreChip =
    isScoreEligibleRequest(request) &&
    request.riskScore &&
    typeof request.riskScore.score === "number";

  const renderDateLocation = () => {
    if (!dateStr && !locationShort) return null;
    return (
      <div className="mt-0.5 truncate text-[10px] text-gray-500">
        {dateStr}
        {dateStr && locationShort ? <span> · </span> : null}
        {locationShort}
      </div>
    );
  };

  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onClick?.(request);
      }}
      className="w-full rounded-md border border-gray-200 bg-white px-2.5 py-2 text-left transition-colors hover:bg-gray-50"
    >
      <div className="flex items-start gap-2">
        <MyVendorRequestBadgeIcon type={request.type} inverted={inverted} />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1 truncate text-xs font-semibold text-gray-900">
              {titleStripped}
            </div>
            {showScoreChip ? (
              <span className="flex-shrink-0">
                <MyVendorRiskScoreBadge
                  score={request.riskScore.score}
                  rating={request.riskScore.rating}
                  size="xs"
                  showRatingText
                  showPercentage
                />
              </span>
            ) : null}
          </div>
          {renderDateLocation()}
          <RequestBadgeChipRow gxps={resolvedGxps} services={request.services} />
        </div>
      </div>
    </button>
  );
}
